import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Download, RefreshCw, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ImageDetailDialog — focused detail view for a rendered image from the Studio feed.
 *
 * Replaces the previous flow where tapping a feed image unfurled the full
 * "Advanced Editor" (with Style Presets etc.) — users wanted a clean detail
 * surface showing just the image, prompt, and the four high-intent actions:
 *   · Download   — fetch & save the file locally
 *   · Private    — toggle Public ↔ Private visibility
 *   · Regenerate — re-run the same prompt via the feed queue
 *   · Create New — close dialog + focus the prompt composer for a fresh gen
 *
 * Open state is driven by `item`; passing `null` closes.
 */
async function downloadUrl(url, filename) {
  try {
    const res = await fetch(url, { credentials: "include" });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(link.href), 5000);
  } catch {
    window.open(url, "_blank");
  }
}

export function ImageDetailDialog({ item, onClose, onRegenerate, onCreateNew }) {
  const [isPublic, setIsPublic] = useState(!!item?.is_public);
  const [togglingVis, setTogglingVis] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Fetch latest visibility when dialog opens — the feed queue doesn't
  // carry `is_public`, so without this call users always see "Private"
  // even for generations they'd already shared.
  useEffect(() => {
    if (!item?.generation_id) return;
    let cancelled = false;
    setIsPublic(!!item.is_public);
    api.get(`/generations/${item.generation_id}`)
      .then(({ data }) => { if (!cancelled && data) setIsPublic(!!data.is_public); })
      .catch(() => {}); // 403/404 → fallback to default
    return () => { cancelled = true; };
  }, [item?.generation_id, item?.is_public]);

  if (!item) return null;

  const togglePublic = async () => {
    if (!item.generation_id || togglingVis) return;
    setTogglingVis(true);
    const next = !isPublic;
    // Optimistic update
    setIsPublic(next);
    try {
      const { data } = await api.patch(`/generations/${item.generation_id}/visibility`, { is_public: next });
      setIsPublic(!!data.is_public);
      toast.success(data.is_public ? "Shared with the dynasty" : "Sealed in your vault");
    } catch {
      setIsPublic(!next); // rollback
      toast.error("Couldn't update visibility.");
    } finally {
      setTogglingVis(false);
    }
  };

  const handleDownload = async () => {
    if (!item.media_url || downloading) return;
    setDownloading(true);
    try {
      await downloadUrl(item.media_url, `geneo-ai-${item.generation_id || "image"}.png`);
      toast.success("Downloaded.");
    } finally {
      setDownloading(false);
    }
  };

  const handleRegenerate = () => {
    onRegenerate?.(item);
    onClose?.();
    toast.success("Re-running the prompt — a fresh take is on the way.");
  };

  const handleCreateNew = () => {
    onCreateNew?.();
    onClose?.();
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="image-detail-dialog"
        className="bg-[#120E15] border-[#F2C94C]/25 text-white max-w-3xl p-0 overflow-hidden"
      >
        {/* Image surface */}
        <div className="relative bg-[#0A0A0A]">
          <img
            src={item.media_url}
            alt={item.prompt || "generation"}
            className="w-full max-h-[65vh] object-contain"
            data-testid="image-detail-image"
          />
          {/* Private/Public pill — floats top-right over the image */}
          <div
            className="absolute top-3 right-3 flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-1.5 rounded-full border border-white/[0.1]"
            data-testid="image-detail-visibility"
          >
            <span className={`font-mono-hy text-[9px] tracking-[0.2em] ${isPublic ? "text-[#27AE60]" : "text-[#F2C94C]"}`}>
              {isPublic ? "◉ PUBLIC" : "○ PRIVATE"}
            </span>
            <Switch
              checked={isPublic}
              disabled={togglingVis}
              onCheckedChange={togglePublic}
              data-testid="image-detail-visibility-toggle"
              aria-label={isPublic ? "Make private" : "Make public"}
            />
          </div>
        </div>

        {/* Prompt + actions */}
        <div className="p-5 space-y-4">
          <div>
            <div className="font-mono-hy text-[9px] text-white/60 tracking-[0.22em] mb-1.5">PROMPT</div>
            <p className="font-body text-sm text-white leading-relaxed" data-testid="image-detail-prompt">
              {item.prompt || <span className="text-white/40 italic">No prompt recorded.</span>}
            </p>
          </div>

          {/* Action row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
            <button
              onClick={handleDownload}
              disabled={downloading || !item.media_url}
              data-testid="image-detail-download-btn"
              className="h-10 px-4 rounded-full border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] hover:shadow-[0_0_18px_-4px_rgba(242,201,76,0.55)] transition-all flex items-center justify-center gap-2 font-mono-hy text-[10px] tracking-[0.18em] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              DOWNLOAD
            </button>

            <button
              onClick={handleRegenerate}
              data-testid="image-detail-regenerate-btn"
              className="h-10 px-4 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] hover:shadow-[0_0_18px_-4px_rgba(0,229,255,0.55)] transition-all flex items-center justify-center gap-2 font-mono-hy text-[10px] tracking-[0.18em]"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              REGENERATE
            </button>

            <button
              onClick={handleCreateNew}
              data-testid="image-detail-create-new-btn"
              className="h-10 px-4 rounded-full bg-gradient-to-r from-[#F2C94C] to-[#E27D60] text-[#0A0A0A] hover:brightness-110 hover:shadow-[0_0_22px_-4px_rgba(242,201,76,0.75)] transition-all flex items-center justify-center gap-2 font-mono-hy text-[10px] tracking-[0.18em] font-bold col-span-2 sm:col-span-1"
            >
              <Plus className="w-3.5 h-3.5" />
              CREATE NEW IMAGE
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImageDetailDialog;
